import { Textarea } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { useEffect, useRef, useState } from 'react';

import { LEAD_DETAIL_DESCRIPTION_CLASS } from '@/core/components/leads/lead-detail/lead-detail-field-styles';

interface LeadDetailDescriptionProps {
  value: string;
  placeholder: string;
  messageFromLead?: string | null;
  messageLabel?: string;
  isSaving: boolean;
  onSave: (value: string) => void;
  className?: string;
}

/** Keep at least one line visible; empty + min-h-0 can clip placeholders (iOS/WebKit). */
const MIN_AUTO_TEXTAREA_HEIGHT_PX = 48;

function syncTextareaHeight(textarea: HTMLTextAreaElement | null) {
  if (!textarea) return;
  textarea.style.height = 'auto';
  const next = Math.max(textarea.scrollHeight, MIN_AUTO_TEXTAREA_HEIGHT_PX);
  textarea.style.height = `${next}px`;
}

export function LeadDetailDescription({
  value,
  placeholder,
  messageFromLead,
  messageLabel,
  isSaving,
  onSave,
  className,
}: LeadDetailDescriptionProps) {
  const [draft, setDraft] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const lastSaved = useRef(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  if (value !== prevValue) {
    setPrevValue(value);
    setDraft(value);
  }

  useEffect(() => {
    lastSaved.current = value;
  }, [value]);

  useEffect(() => {
    syncTextareaHeight(textareaRef.current);
  }, [draft, value]);

  const handleBlur = () => {
    const trimmed = draft.trim();
    if (trimmed === lastSaved.current.trim()) return;
    lastSaved.current = trimmed;
    onSave(trimmed);
  };

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <Textarea
        ref={textareaRef}
        value={draft}
        rows={1}
        onChange={(e) => {
          setDraft(e.target.value);
          syncTextareaHeight(e.target);
        }}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={isSaving}
        className={LEAD_DETAIL_DESCRIPTION_CLASS}
      />
      {messageFromLead?.trim() ? (
        <div className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-2.5">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
            {messageLabel}
          </p>
          <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-foreground">
            {messageFromLead}
          </p>
        </div>
      ) : null}
    </section>
  );
}
